import type { Concept, Location, RestaurantShim } from "@mmh/types"
import { computed, inject, onMounted, ref, type InjectionKey } from "vue"
import { getApiBaseUrl } from "~/lib/api"
import { normalizeLocationPath } from "./locations"

export const ConceptKey: InjectionKey<Concept> = Symbol("concept")

export const RestaurantShimKey: InjectionKey<RestaurantShim> = Symbol("restaurantShim")


export function useApiConfig(locationPath?: string) {
    const staticConcept = inject(ConceptKey)

    if (!staticConcept) throw new Error("Concept not provided")

    const storageKey = `etag:${staticConcept.slug}`
    const timestampKey = `${storageKey}:timestamp`
    const dataKey = `${storageKey}:data`
    const MAX_CACHE_AGE = 24 * 60 * 60 * 1000 * 60
    
    const getCachedETag = (): string | null => {
        if (typeof window === "undefined") return null
        
        try {
            const etag = localStorage.getItem(storageKey)
            const timestamp = localStorage.getItem(timestampKey)
            
            if (!etag || !timestamp) return null
            
            const age = Date.now() - parseInt(timestamp, 10)
            if (age > MAX_CACHE_AGE) {
                localStorage.removeItem(storageKey)
                localStorage.removeItem(timestampKey)
                localStorage.removeItem(dataKey)
                return null
            }
            
            return etag
        } catch (error) {
            console.error("Failed to read from localStorage:", error)
            return null
        }
    }
    
    const getCachedData = (): Concept | null => {
        if (typeof window === "undefined") return null
        
        try {
            const dataStr = localStorage.getItem(dataKey)
            const timestamp = localStorage.getItem(timestampKey)
            
            if (!dataStr || !timestamp) return null
            
            const age = Date.now() - parseInt(timestamp, 10)
            if (age > MAX_CACHE_AGE) {
                return null
            }
            
            return JSON.parse(dataStr) as Concept
        } catch (error) {
            console.error("Failed to read cached data from localStorage:", error)
            return null
        }
    }
    
    const cachedETag = ref<string | null>(getCachedETag())
    
    const freshData = ref<Concept | null>(getCachedData() || staticConcept)
    const isLoading = ref(false)
    const lastChecked = ref<Date | null>(null)

    const concept = computed(() => freshData.value || staticConcept)

    async function checkForUpdates(): Promise<boolean> {
        if (typeof window === "undefined" || !staticConcept?.slug) return false

        try {
            const apiUrl = getApiBaseUrl()
            const url = `${apiUrl}/config/concepts/${staticConcept.slug}`

            const headers: HeadersInit = {
                "Cache-Control": "no-cache"
            }
            if (cachedETag.value) {
                headers["If-None-Match"] = cachedETag.value
            }

            const response = await fetch(url, { 
                headers,
                cache: "no-cache"
            })
            lastChecked.value = new Date()

            if (response.status === 304) {
                return false
            }

            if (response.ok) {
                const newETag = response.headers.get("ETag")
                const data = await response.json()
                
                cachedETag.value = newETag
                freshData.value = data
                
                if (newETag) {
                    try {
                        localStorage.setItem(storageKey, newETag)
                        localStorage.setItem(timestampKey, Date.now().toString())
                        localStorage.setItem(dataKey, JSON.stringify(data))
                    } catch (error) {
                        console.error("Failed to write to localStorage:", error)
                    }
                }
                return true
            }
        } catch (error) {
            console.error("Failed to check for updates, using static data", error)
        }

        return false
    }

    async function fetchFreshData(): Promise<void> {
        if (typeof window === "undefined" || !staticConcept?.slug) return

        try {
            isLoading.value = true
            
            const apiUrl = getApiBaseUrl()
            const url = `${apiUrl}/config/concepts/${staticConcept.slug}`
            
            const response = await fetch(url)
            
            if (response.ok) {
                const newETag = response.headers.get("ETag")
                const data = await response.json()
                
                cachedETag.value = newETag
                freshData.value = data
                
                if (newETag) {
                    try {
                        localStorage.setItem(storageKey, newETag)
                        localStorage.setItem(timestampKey, Date.now().toString())
                        localStorage.setItem(dataKey, JSON.stringify(data))
                    } catch (error) {
                        console.error("Failed to write to localStorage:", error)
                    }
                }
            }
        } catch (error) {
            console.error("Failed to fetch fresh data, using static data", error)
        } finally {
            isLoading.value = false
        }
    }

    onMounted(() => {
        isLoading.value = true
        checkForUpdates().finally(() => {
            isLoading.value = false
        })
    })

    const location = computed<Location | undefined>(() => {
        if (!concept.value.locations || concept.value.locations.length === 0) {
            return undefined
        }

        if (locationPath) {
            // TODO: Remove once database is updated to use the correct paths
            // PATCH: Fix 'costa-mesa' path in prod db to match config path of 'costamesa'
            return concept.value.locations.find((loc: Location) => 
                normalizeLocationPath(loc.path) === locationPath
            )
        }

        return concept.value.locations[0]
    })

    const services = computed(() => {
        return location.value?.services || []
    })
    const menus = computed(() => {
        return location.value?.menus || {}
    })

    const hours = computed(() => {
        return location.value?.hours || []
    })
    const pageContent = computed(() => {
        return location.value?.pageContent
    })

    const faqs = computed(() => {
        return location.value?.pageContent?.faqs || []
    })

    const contact = computed(() => {
        return location.value?.contact
    })
    const address = computed(() => {
        return location.value?.address
    })

    const external = computed(() => {
        return location.value?.external
    })

    return {
        concept,
        location,

        isLoading,
        lastChecked,
        hasFreshData: computed(() => freshData.value !== null),

        checkForUpdates,
        fetchFreshData,

        // Location-specific data
        services,
        menus,
        hours,
        pageContent,
        faqs,
        contact,
        address,
        external,

        conceptName: concept.value.name,
        conceptSlug: concept.value.slug,
        conceptTagline: concept.value.tagline,
        conceptImages: concept.value.images,
        conceptSocialMedia: concept.value.socialMedia,
        parentCompany: concept.value.parentCompany
    }
}

export type ApiConfig = ReturnType<typeof useApiConfig>